import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, mergeMap, tap } from 'rxjs';

import { HealthService } from '../../services/health/health.service';
import { metricsQueryKey } from '../../services/health/health.key';
import { HealthActions } from './health.actions';
import { CameraActions } from '../camera/camera.actions';
import { UserActions } from '../user/user.actions';

/**
 * The action -> effect half of health telemetry: components dispatch, these
 * delegate into HealthService, and the service's signals are what templates
 * read. The acknowledgements carry at most a key - the data never enters the store.
 */
@Injectable()
export class HealthEffects {
  private actions$ = inject(Actions);
  private healthService = inject(HealthService);

  watchFleet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.watchFleet),
      tap(() => this.healthService.watchFleet()),
      map(() => HealthActions.fleetWatchStarted()),
    ),
  );

  unwatchFleet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.unwatchFleet),
      tap(() => this.healthService.unwatchFleet()),
      map(() => HealthActions.fleetWatchStopped()),
    ),
  );

  watchTick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.watchTick),
      tap(() => this.healthService.watchTick()),
      map(() => HealthActions.tickWatchChanged()),
    ),
  );

  unwatchTick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.unwatchTick),
      tap(() => this.healthService.unwatchTick()),
      map(() => HealthActions.tickWatchChanged()),
    ),
  );

  watchRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.watchRefresh),
      tap(() => this.healthService.watchRefresh()),
      map(() => HealthActions.refreshWatchChanged()),
    ),
  );

  unwatchRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.unwatchRefresh),
      tap(() => this.healthService.unwatchRefresh()),
      map(() => HealthActions.refreshWatchChanged()),
    ),
  );

  // Dispatch is synchronous, so a key is held before the query that fills it is dispatched.
  retainQueries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.retainQueries),
      tap(({ keys }) => keys.forEach((key) => this.healthService.retainQuery(key))),
      map(() => HealthActions.queriesRetained()),
    ),
  );

  releaseQueries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.releaseQueries),
      tap(({ keys }) => keys.forEach((key) => this.healthService.releaseQuery(key))),
      map(() => HealthActions.queriesReleased()),
    ),
  );

  refreshFleet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.refreshFleet),
      tap(() => this.healthService.refreshFleet()),
      map(() => HealthActions.fleetRefreshDone()),
    ),
  );

  // The API scopes every answer to the token's organisation, so a new login
  // must not be served what the last one fetched.
  clearOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.logout),
      tap(() => this.healthService.reset()),
      map(() => HealthActions.cacheCleared()),
    ),
  );

  // A live snapshot pull is evidence the camera answered right now - minutes
  // before the fleet poll or the metrics rollup could know. Cache replays
  // carry live=false and prove nothing.
  snapshotEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CameraActions.loadSnapshotSuccess, CameraActions.loadSnapshotFailure),
      filter((action) => action.live),
      tap((action) =>
        this.healthService.recordSnapshotEvidence(
          action.sensor.id,
          action.type === CameraActions.loadSnapshotSuccess.type,
        ),
      ),
      map((action) =>
        HealthActions.snapshotEvidenceRecorded({
          cameraId: action.sensor.id,
          ok: action.type === CameraActions.loadSnapshotSuccess.type,
        }),
      ),
    ),
  );

  /** primeSnapshotFromLivestream only leaves the player after a frame
   * rendered, so it needs no live flag - a captured frame is proof of
   * connectivity, and a failed start is a failed reach. */
  livestreamEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CameraActions.primeSnapshotFromLivestream,
        CameraActions.startCameraLivestreamFailure,
      ),
      map((action) =>
        action.type === CameraActions.primeSnapshotFromLivestream.type
          ? { cameraId: action.sensorId, ok: true }
          : { cameraId: action.sensorID, ok: false },
      ),
      tap(({ cameraId, ok }) =>
        this.healthService.recordSnapshotEvidence(cameraId, ok),
      ),
      map(({ cameraId, ok }) =>
        HealthActions.snapshotEvidenceRecorded({ cameraId, ok }),
      ),
    ),
  );

  // Results land on the service inside runMetricsQuery's pipe; shareReplay
  // still dedupes, so re-dispatching an in-flight key costs nothing.
  metricsQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HealthActions.runMetricsQuery),
      mergeMap(({ name, params }) =>
        this.healthService.runMetricsQuery(name, params).pipe(
          map(() =>
            HealthActions.metricsQueryDone({ key: metricsQueryKey(name, params) }),
          ),
        ),
      ),
    ),
  );
}
